/**
 * UserContext - Shared User profile and role management
 *
 * Manages:
 * - User profile data (name, bio, preferences, etc.)
 * - User role (admin level)
 * - Profile editing state
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import * as api from '../api';

export interface UserProfile {
  user_id: string;
  email: string;
  given_name?: string;
  family_name?: string;
  bio?: string | null;
  phone_number?: string | null;
  position?: string | null;
  jersey_number?: string | null;
  height?: string | null;
  weight?: string | null;
  date_of_birth?: string | null;
  role?: string;
  platform_role?: string;  // USER, SUPPORT, SUPER_ADMIN
  profile_picture_url?: string | null;
  // Coach-specific
  certifications?: string[];
  years_experience?: number | null;
  specialties?: string[];
  timezone?: string | null;
  // Timestamps
  created_at?: string;
  updated_at?: string;
  // Notification preferences
  notify_on_comment?: boolean;
  notify_on_feedback?: boolean;
  notify_on_assignment?: boolean;
  // Admin notification preferences
  admin_notify_new_user?: boolean;
  admin_notify_new_org?: boolean;
  admin_notify_high_usage?: boolean;
}

interface UserContextType {
  // State
  userProfile: UserProfile | null;
  userRole: string;
  isLoading: boolean;
  isNewUser: boolean;
  needsOnboarding: boolean;

  // Computed
  isSuperAdmin: boolean;
  isAdmin: boolean;
  displayName: string;
  userId: string | null;

  // Actions
  loadUserProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<boolean>;
  setUserProfile: (profile: UserProfile | null) => void;
  clearNewUserFlag: () => void;
}

const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  // userRole here refers to platform_role (USER, SUPPORT, SUPER_ADMIN)
  const [userRole, setUserRole] = useState<string>('USER');
  const [isLoading, setIsLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const loadUserProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.getProfile();
      setUserProfile(response.user as UserProfile);
      // Use platform_role for admin status (USER, SUPPORT, SUPER_ADMIN)
      setUserRole(response.user.platform_role || 'USER');
      // Track new user and onboarding status
      setIsNewUser(response.is_new_user);
      setNeedsOnboarding(response.needs_onboarding);
    } catch (err) {
      console.error('Failed to load user profile:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (updates: Partial<UserProfile>): Promise<boolean> => {
    try {
      const { user } = await api.updateProfile(updates);
      setUserProfile(user as UserProfile);
      // After updating profile, check if onboarding is still needed
      const hasName = Boolean(user.given_name && user.given_name.trim());
      setNeedsOnboarding(!hasName);
      return true;
    } catch (err) {
      console.error('Failed to update profile:', err);
      return false;
    }
  }, []);

  const clearNewUserFlag = useCallback(() => {
    setIsNewUser(false);
  }, []);

  // Computed values based on platform_role
  const isSuperAdmin = userRole === 'SUPER_ADMIN';
  const isAdmin = userRole === 'SUPER_ADMIN' || userRole === 'SUPPORT';
  const displayName = userProfile
    ? `${userProfile.given_name || ''} ${userProfile.family_name || ''}`.trim() || userProfile.email
    : '';
  const userId = userProfile?.user_id ?? null;

  const value: UserContextType = {
    userProfile,
    userRole,
    isLoading,
    isNewUser,
    needsOnboarding,
    isSuperAdmin,
    isAdmin,
    displayName,
    userId,
    loadUserProfile,
    updateProfile,
    setUserProfile,
    clearNewUserFlag,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
